/**
 * src/app/venue/NDASplash.tsx — 2004va
 *
 * Shown every time a picker enters /venue. Makes the "closed demo, not
 * affiliated, don't circulate" framing explicit before they see any content.
 *
 * Design decision: always-shown rather than once-per-browser, because a
 * forgotten dismissal would weaken the legal posture if the demo leaked.
 * For a prototype this scale, the small UX cost is worth it. Revisit when
 * moving to a real production venue where pickers log in daily.
 */

import { useState } from 'react'
import { ShieldAlert } from 'lucide-react'

interface NDASplashProps {
  venueName: string
  onAccept:  () => void
}

export function NDASplash({ venueName, onAccept }: NDASplashProps) {
  const [acked, setAcked] = useState(false)

  return (
    <div className="fixed inset-0 z-50 bg-room-bg flex items-center justify-center px-5">
      <div className="max-w-md w-full space-y-6">

        {/* Icon */}
        <div className="flex justify-center">
          <div className="w-14 h-14 rounded-2xl bg-room-gold/15 border border-room-gold/40 flex items-center justify-center">
            <ShieldAlert size={26} className="text-room-gold" />
          </div>
        </div>

        {/* Heading */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-white">Closed demonstration</h1>
          <p className="text-sm text-room-muted">
            You are about to view a prototype environment.
          </p>
        </div>

        {/* Body */}
        <div className="bg-room-surface border border-room-border rounded-2xl p-5 space-y-3 text-sm text-white/85 leading-relaxed">
          <p>
            This is a closed demonstration of a platform in development,
            shared with you under confidence.
          </p>
          <p>
            It is <strong className="text-white">not affiliated with {venueName}</strong>,
            Purple Wall Music, Claptone, or any associated party. Names and
            references are placeholders used to model real-world workflows.
          </p>
          <p>
            Please do not circulate, screenshot for public posting, or share
            access with anyone outside the intended viewing circle.
          </p>
        </div>

        {/* Ack */}
        <button
          type="button"
          onClick={() => setAcked(v => !v)}
          className="w-full flex items-start gap-3 p-4 rounded-2xl bg-room-surface border border-room-border hover:border-room-accent/40 transition-colors text-left"
        >
          <div className={[
            'w-6 h-6 rounded-md border-2 flex-shrink-0 mt-0.5 flex items-center justify-center',
            acked ? 'bg-room-accent border-room-accent' : 'border-room-muted bg-transparent',
          ].join(' ')}>
            {acked && (
              <svg className="w-4 h-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <span className="text-sm text-white leading-snug">
            I understand this is a closed demonstration and I will not share
            access or circulate any content outside the agreed viewing circle.
          </span>
        </button>

        {/* Enter */}
        <button
          type="button"
          disabled={!acked}
          onClick={onAccept}
          className={[
            'w-full py-4 rounded-2xl font-bold text-base transition-all min-h-[48px]',
            acked
              ? 'bg-white text-room-bg hover:bg-white/90 shadow-lg'
              : 'bg-room-surface text-room-muted cursor-not-allowed border border-room-border',
          ].join(' ')}
        >
          {acked ? 'Enter' : 'Acknowledge to continue'}
        </button>
      </div>
    </div>
  )
}
